// PRD.310 - Visit Note Medical History section.
//
// A GENERIC Visit Note sub-module: one multiline narrative field scoped to the
// ACTIVE ENCOUNTER (`patient_encounters.medical_history`), with dictation.
//
// What it explicitly does NOT do: it never writes to Allergies, Family History,
// Problems/Conditions, medications or Prescriptions, and it is never promoted
// to a patient-level canonical field. Each encounter documents the history as
// stated at that visit and stays historically independent.
//
// PRD.312 added the read-only longitudinal context and the clinic capability
// switches. PRD.313 turned that into one continuing-document surface. PRD.314
// extracted the editor into `HistoryEntryEditor` so Specialty History shares it
// rather than copying it - the persistence path below is unchanged.

import { useCallback, useMemo } from 'react';
import {
  getMedicalHistorySettings,
  qAppConfig,
  type AppConfigRow,
  type PatientEncounterRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { HistoryEntryEditor } from './HistoryEntryEditor'; // PRD.314
import { LongitudinalHistoryEditor } from './LongitudinalHistoryEditor'; // PRD.313
import { toLongitudinalEntries } from './longitudinal-history-logic';
import { fromStoredMedicalHistory, toStoredMedicalHistory } from './medical-history-logic';
import { formatVisitDate } from './patient-image-visits';
import { useMedicalHistoryContext } from './useMedicalHistoryContext'; // PRD.312

export function MedicalHistorySection({
  encounter,
  patientId,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();

  // PRD.312 - clinic/org capability settings (app_config.extra_json), NOT
  // section visibility. Whether this section renders at all is still the user's
  // Visit Note preference (PRD.300) over the Guided preset default (PRD.310);
  // these only decide what the visible section offers. Both default ON, so a
  // clinic that has never saved them keeps the full PRD.310 behavior.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const settings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { patientHistory, priorEntries } = useMedicalHistoryContext(
    patientId,
    encounter.id,
    settings.showPreviousHistory,
  );

  // PRD.313 5.2 - prior entries arrive oldest -> newest, so the history reads
  // forwards in time into today's editable entry at the bottom.
  const entries = useMemo(
    () => toLongitudinalEntries(priorEntries, formatVisitDate),
    [priorEntries],
  );

  // The PRD.310 write path, unchanged - a targeted UPDATE on the encounter row.
  const saveCurrent = useCallback(
    async (text: string, encounterId: string) => {
      if (!db) return;
      await db.execute(
        'UPDATE patient_encounters SET medical_history = ?, updated_at = ? WHERE id = ?',
        [toStoredMedicalHistory(text), new Date().toISOString(), encounterId],
      );
    },
    [db],
  );

  // No inner heading - the surrounding layout (tab label, accordion header, or
  // guided-note card title) already says "Medical History".
  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        // Claim the column's full height (a no-op outside the grid) so the
        // editor below can grow into whatever space the row has.
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <LongitudinalHistoryEditor
        label="Medical History"
        patientRecord={patientHistory}
        previousEntries={entries}
        showHistory={settings.showPreviousHistory}
      >
        <HistoryEntryEditor
          identity={encounter.id}
          storedValue={fromStoredMedicalHistory(encounter.medical_history)}
          onSave={saveCurrent}
          label="Medical History"
          placeholder="Add to medical history..."
          voiceEnabled={settings.voiceToTextEnabled}
        />
      </LongitudinalHistoryEditor>
    </div>
  );
}
